/**
 * Card Payment Service
 * Handles all card payment operations via Paystack integration
 */

const BASE_URL = 'https://svr.pegasus-pms.co.ke/api/pegasus/visionary';

/**
 * Initialize a new card payment for rent
 * @param {Object} params - Payment parameters
 * @param {number} params.tenantId - The tenant's ID
 * @param {number} params.propertyId - The property ID
 * @param {number} params.amount - Amount in KES
 * @param {string} params.email - Email for Paystack receipt
 * @param {boolean} params.saveCard - Whether to save card for future payments
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} - Returns authorization_url, reference, access_code, public_key
 */
export const initializeCardPayment = async ({ tenantId, propertyId, amount, email, saveCard = false, token }) => {
    const response = await fetch(`${BASE_URL}/payments/card/initialize`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'token': token,
        },
        body: JSON.stringify({
            tenant_id: tenantId,
            property_id: propertyId,
            amount: amount,
            email: email,
            save_card: saveCard,
        }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new CardPaymentError(data.message || 'Failed to initialize payment', response.status, data);
    }

    return data;
};

/**
 * List all saved cards for a tenant
 * @param {Object} params - Request parameters
 * @param {number} params.tenantId - The tenant's ID
 * @param {string} params.token - Authentication token
 * @returns {Promise<Array>} - Returns array of saved cards
 */
export const getSavedCards = async ({ tenantId, token }) => {
    const response = await fetch(`${BASE_URL}/payments/card/cards/list`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'token': token,
        },
        body: JSON.stringify({ tenant_id: tenantId }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new CardPaymentError(data.message || 'Failed to fetch saved cards', response.status, data);
    }

    return data.success ? data.cards : [];
};

/**
 * Charge a saved card for rent payment
 * @param {Object} params - Charge parameters
 * @param {number} params.cardTokenId - The saved card ID
 * @param {number} params.tenantId - The tenant's ID
 * @param {number} params.propertyId - The property ID
 * @param {number} params.amount - Amount in KES
 * @param {string} params.description - Payment description
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} - Returns success status, reference, amount
 */
export const chargesSavedCard = async ({ cardTokenId, tenantId, propertyId, amount, description, token }) => {
    const response = await fetch(`${BASE_URL}/payments/card/charge/recurring`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'token': token,
        },
        body: JSON.stringify({
            card_token_id: cardTokenId,
            tenant_id: tenantId,
            property_id: propertyId,
            amount: amount,
            description: description,
        }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new CardPaymentError(data.message || 'Failed to charge card', response.status, data);
    }

    return data;
};

/**
 * Delete a saved card
 * @param {Object} params - Delete parameters
 * @param {number} params.cardTokenId - The saved card ID
 * @param {number} params.tenantId - The tenant's ID
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} - Returns success status
 */
export const deleteSavedCard = async ({ cardTokenId, tenantId, token }) => {
    const response = await fetch(`${BASE_URL}/payments/card/cards/delete`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'token': token,
        },
        body: JSON.stringify({
            card_token_id: cardTokenId,
            tenant_id: tenantId,
        }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new CardPaymentError(data.message || 'Failed to delete card', response.status, data);
    }

    return data;
};

/**
 * Custom error class for card payment errors
 */
export class CardPaymentError extends Error {
    constructor(message, statusCode, data) {
        super(message);
        this.name = 'CardPaymentError';
        this.statusCode = statusCode;
        this.data = data;
    }
}

/**
 * Get user-friendly error message for card decline reasons
 * @param {string} reason - The decline reason from API
 * @returns {string} - User-friendly message
 */
export const getCardDeclineMessage = (reason) => {
    const declineMessages = {
        'insufficient_funds': 'Your card has insufficient funds. Please try another card.',
        'card_expired': 'This card has expired. Please use a different card.',
        'invalid_cvv': 'Card details are incorrect. Please check and try again.',
        'card_blocked': 'This card cannot be used. Please contact your bank.',
        'declined': 'Payment declined by your bank. Please try another card.',
    };

    const lowerReason = reason?.toLowerCase() || '';

    for (const [key, message] of Object.entries(declineMessages)) {
        if (lowerReason.includes(key.replace('_', ' ')) || lowerReason.includes(key)) {
            return message;
        }
    }

    return reason || 'Payment failed. Please try again.';
};

/**
 * Get card type icon name based on card_type
 * @param {string} cardType - The card type (visa, mastercard, verve)
 * @returns {string} - Icon identifier
 */
export const getCardTypeIcon = (cardType) => {
    const cardIcons = {
        'visa': 'visa',
        'mastercard': 'mastercard',
        'verve': 'verve',
    };
    return cardIcons[cardType?.toLowerCase()] || 'card';
};

export default {
    initializeCardPayment,
    getSavedCards,
    chargesSavedCard,
    deleteSavedCard,
    CardPaymentError,
    getCardDeclineMessage,
    getCardTypeIcon,
};
